// Remove background on a clip (spec remove-background.md): Studio's Edit Background
// (`video.background`) on the clip's file, Bria with a green background and the clip's sound kept,
// free as live. Each run is followed by the stream (its job's query refetched while the stream is
// down, or while reading it fails); once it succeeds, the clip plays its output, keyed
// (`removeBackground`), through the editor as it is then. The editor holds one run a clip, kept
// while another clip is chosen; a run lives with the editor's tab (decision 3). A start whose answer
// was lost is sent again with its key, so the server answers with the job it already started.
import { useQueries } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { getGetJobQueryOptions, useStartJob } from "../../api/generated/endpoints/generation/generation";
import { ApiError } from "../../api/errors";
import type { Job, JobRequest } from "../../api/generated/model";
import { attemptKey, lostAttempt, type LostAttempt } from "../../platform/idempotency";
import { FALLBACK_REFETCH_MS, canChange, jobRefetchInterval } from "../../platform/jobs/refetch";
import { toUserMessage } from "../../platform/messages";
import { useStreamStatus } from "../../platform/stream-status";
import type { TimelineEdit, VideoClip } from "./timeline";
import { toWireSource } from "./wire";

/** Bria's row: a background colour to key, and the clip's sound kept (decision 1). */
export const BACKGROUND_MODEL = "bria-video-background-removal";

/** A clip's run: starting, its job running, or failed with its reason. */
export type BackgroundRun =
  | { state: "starting" }
  | { state: "running"; jobId: string }
  | { state: "failed"; reason: string };

export interface BackgroundRuns {
  /** The clip's run, or null with none. */
  runOf: (clipId: string) => BackgroundRun | null;
  /** Starts a run on the video's file. */
  remove: (clip: VideoClip) => void;
}

/** The body that removes a video's background: Bria, green, its sound kept. */
export function backgroundRequest(clip: VideoClip): JobRequest {
  return {
    type: "video.background",
    model: BACKGROUND_MODEL,
    source_video: toWireSource(clip.source),
    background_color: "Green",
    preserve_audio: true,
  };
}

/** Why a job ended without its video, in its own words. */
function reasonOf(job: Job): string {
  return job.error?.message ?? "The job ended without a video.";
}

/** A run's end: the clip's output when its job succeeded with one, else why not. */
interface Ended {
  clipId: string;
  jobId: string;
  made: boolean;
  reason: string;
}

/**
 * When a run's job is read again: as any job's, and every 15 s while reading it fails, stream or
 * not, since a job that ended meanwhile sends no other event.
 */
function readAgain(status: Job["status"] | undefined, failed: boolean, stream: ReturnType<typeof useStreamStatus>): number | false {
  return failed ? FALLBACK_REFETCH_MS : jobRefetchInterval(status, stream);
}

export function useRemoveBackground(options: {
  teamId: string;
  brandId: string;
  onEdit: (edit: TimelineEdit) => void;
}): BackgroundRuns {
  const { teamId, brandId } = options;
  const stream = useStreamStatus();
  const start = useStartJob();
  const [runs, setRuns] = useState<ReadonlyMap<string, BackgroundRun>>(new Map());
  // Each clip's start whose answer was lost: tried again with the same body, it keeps its key.
  const lost = useRef(new Map<string, LostAttempt>());
  // The editor as it is now, for a run that ends later.
  const latest = useRef(options);
  useEffect(() => {
    latest.current = options;
  });
  const running = [...runs].flatMap(([clipId, run]) => (run.state === "running" ? [{ clipId, jobId: run.jobId }] : []));
  const jobs = useQueries({
    queries: running.map(({ jobId }) => ({
      ...getGetJobQueryOptions(teamId, jobId),
      refetchInterval: (query: { state: { data: Job | undefined; status: string } }) =>
        readAgain(query.state.data?.status, query.state.status === "error", stream),
    })),
  });
  const ended = running.flatMap(({ clipId, jobId }, at): Ended[] => {
    const read = jobs[at];
    const job = read?.data;
    // The stream writes a job's new status at once and its outputs and error come with the read
    // after (`event-handlers.ts`): its end counts once a read of it has landed.
    const landed = read?.status === "success" && !read.isFetching;
    if (job !== undefined && landed && !canChange(job.status)) {
      const made = job.status === "succeeded" && job.outputs.length > 0;
      return [{ clipId, jobId, made, reason: made ? "" : reasonOf(job) }];
    }
    // A read the server refuses for good (the job gone, or not the member's) ends the run; one that
    // failed for now is read again (`readAgain`).
    if (read?.error instanceof ApiError && read.error.status < 500) {
      return [{ clipId, jobId, made: false, reason: toUserMessage(read.error) }];
    }
    return [];
  });
  // The runs ended as of the last render, for the effect that lands each once.
  const endedNow = useRef(ended);
  useEffect(() => {
    endedNow.current = ended;
  });
  const endedKey = ended.map(({ jobId }) => jobId).join(" ");
  useEffect(() => {
    for (const { clipId, jobId, made, reason } of endedNow.current) {
      // A clip gone or locked meanwhile refuses it, and nothing changes.
      if (made) latest.current.onEdit({ type: "removeBackground", id: clipId, source: { kind: "output", jobId, index: 0 } });
      setRuns((current) => {
        const run = current.get(clipId);
        if (run?.state !== "running" || run.jobId !== jobId) return current;
        const next = new Map(current);
        if (made) next.delete(clipId);
        else next.set(clipId, { state: "failed", reason });
        return next;
      });
    }
  }, [endedKey]);
  const set = (clipId: string, run: BackgroundRun) => setRuns((current) => new Map(current).set(clipId, run));
  return {
    runOf: (clipId) => runs.get(clipId) ?? null,
    remove: (clip) => {
      const data = backgroundRequest(clip);
      const key = attemptKey(data, lost.current.get(clip.id));
      set(clip.id, { state: "starting" });
      start
        .mutateAsync({ teamId, brandId, data, headers: { "Idempotency-Key": key } })
        .then((job) => {
          lost.current.delete(clip.id);
          set(clip.id, { state: "running", jobId: job.id });
        })
        .catch((error: unknown) => {
          const missed = lostAttempt(key, data, error);
          if (missed === undefined) lost.current.delete(clip.id);
          else lost.current.set(clip.id, missed);
          set(clip.id, { state: "failed", reason: toUserMessage(error) });
        });
    },
  };
}
